import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { SCENE_KEYS } from './scene-keys.js';
import { dataManager, DATA_MANAGER_STORE_KEYS } from '../utils/data-manager.js';
import { netManager } from '../net/index.js';
import { fetchDailyQuests } from '../net/api.js';
import { playerLevel, xpForLevel } from '@mn/shared';

// ── Persistent, mouse/touch-driven Monstara overworld HUD (runs OVER the world scene). ──
// A premium, pixel-game HUD laid out to a clean Pokémon-style reference:
//   • top-left    → player card (avatar + name + Lv badge + HP bar + XP bar)
//   • left column → vertical icon menu (Bag / Team / Quests / Map / Settings)
//   • bottom-left → party bar (mini monster chips: sprite + name + Lv)
//   • bottom-right→ "Current Quest" panel (eyebrow + objective + map-pin)
// Location + time + Gold live in the parallel currency HUD (top-right).
//
// Every menu button calls a public WorldScene.openX() method so the launch/pause behavior is
// shared with the keyboard WorldMenu. Everything anchors to the viewport corners and reflows on
// RESIZE; data regions live-update on the relevant dataManager change events. The HUD never
// blocks world taps — its reserved screen bands are mirrored in WorldScene.#isPointerOverHud.
//
// Style: dark rounded glass panels, emerald #34d399 + gold #f5b23e accents, crisp pixels.

// Cozy pixel-RPG palette (matches utils/ui-button.js UI) — warm wood panels + natural accents, not
// the old translucent emerald/gold "glass" that clashed with the pixel world.
const EMERALD = 0x86c06a;
const GOLD = 0xe0b15a;
const GLASS = 0x2a2018;
const INK = 0x140f0a; // chunky outer border
const HP_GREEN = 0x34d399;
const XP_BLUE = 0x3ad0ff;

const PAD = 14;
const RADIUS = 14;

/** Onboarding objective chain — drives the "Current Quest" panel. The first incomplete step is
 *  the active goal (mirrors the persisted progress counters in dataManager). */
const OBJECTIVE_CHAIN = [
  { label: 'Catch your first beast', done: (p) => p.monstersCaught >= 1, progress: (p) => `${Math.min(p.monstersCaught, 1)}/1` },
  { label: 'Win 3 battles', done: (p) => p.battlesWon >= 3, progress: (p) => `${Math.min(p.battlesWon, 3)}/3` },
  { label: 'Reach 5,000 Gold', done: () => dataManager.getGold() >= 5000, progress: () => `${Math.min(dataManager.getGold(), 5000).toLocaleString()}/5,000` },
  { label: 'Order a real card', done: (p) => p.cardsOrdered >= 1, progress: (p) => `${Math.min(p.cardsOrdered, 1)}/1` },
];

/** Left vertical icon menu → public WorldScene methods. Big tap targets, icon + label. */
const MENU_ITEMS = [
  { icon: '🎒', label: 'BAG', method: 'openInventory' },
  { icon: '🐾', label: 'TEAM', method: 'openParty' },
  { icon: '📦', label: 'PC BOX', method: 'openPcBox' },
  { icon: '📜', label: 'QUESTS', method: 'openQuests' },
  { icon: '🎴', label: 'CARDS', method: 'openCardShop' },
  { icon: '🔒', label: 'VAULT', method: 'openVault' },
  { icon: '🛒', label: 'MARKET', method: 'openMarket' },
  { icon: '🗺', label: 'MAP', method: 'openMap' },
  { icon: '🏆', label: 'RANKS', method: 'openLeaderboard' },
  { icon: '👥', label: 'FRIENDS', method: 'openFriends' },
  { icon: '👤', label: 'PROFILE', method: 'openProfile' },
  { icon: '⚙', label: 'SETTINGS', method: 'openSettings' },
  { icon: '❓', label: 'HOW TO', method: 'openHowToPlay' },
];

const CARD_W = 264;
const CARD_H = 96;
const AVATAR = 56;
const ICON_BTN = 56;
const ICON_GAP = 10;
// Widened from 132 → 158 so full monster names fit (name area was only ~66px → "Emberling",
// "Obsidrake", "Aurorath" etc. were truncated to "EMBER…"). 158 gives ~92px of name room.
const CHIP_W = 236;
const CHIP_H = 60;
const CHIP_GAP = 10;
// Party dropdown: a compact toggle button (TEAM N/6 ▾) that expands/collapses the vertical chip list.
const PARTY_HEADER_H = 44;
const PARTY_HEADER_GAP = 8;
// The currency panel (96px) + minimap (132px) live top-RIGHT in the SEPARATE currency-hud scene; the
// team dropdown anchors on the right edge BELOW that band so it never overlaps them.
const TOP_RIGHT_BAND = 248;
const QUEST_W = 300;
const QUEST_H = 92;

export class WorldHudScene extends Phaser.Scene {
  /** @type {Phaser.GameObjects.Container} top-left player card */
  #playerCard;
  /** @type {Phaser.GameObjects.Image} */
  #avatarImg;
  /** @type {Phaser.GameObjects.Text} */
  #nameText;
  /** @type {Phaser.GameObjects.Text} */
  #lvText;
  /** @type {Phaser.GameObjects.Graphics} */
  #hpBar;
  /** @type {Phaser.GameObjects.Graphics} */
  #xpBar;
  /** @type {Phaser.GameObjects.Text} */
  #hpText;
  /** @type {Phaser.GameObjects.Text} */
  #xpText;
  /** @type {Phaser.GameObjects.Container} left vertical icon menu */
  #menuColumn;
  /** @type {Phaser.GameObjects.Container[]} icon buttons inside #menuColumn (repositioned on layout) */
  #menuButtons = [];
  /** @type {Phaser.GameObjects.Container} bottom-left party bar */
  #partyBar;
  /** @type {Phaser.GameObjects.Container} bottom-right current-quest panel */
  #questPanel;
  /** @type {boolean} party dropdown expanded (default open so the team is visible; tap header to collapse) */
  #partyExpanded = true;
  /** @type {Phaser.GameObjects.Text} */
  #questText;

  constructor() {
    super({ key: SCENE_KEYS.WORLD_HUD_SCENE });
  }

  /** @returns {void} */
  create() {
    // Each region is built defensively: a runtime failure in one (e.g. a missing texture/frame)
    // must NEVER blank the whole HUD. Failures are logged and the rest still renders.
    const guard = (fn, label) => {
      try {
        fn();
      } catch (e) {
        console.warn(`[world-hud] ${label} failed`, e);
      }
    };
    guard(() => this.#buildPlayerCard(), 'buildPlayerCard');
    guard(() => this.#buildMenuColumn(), 'buildMenuColumn');
    guard(() => this.#buildPartyBar(), 'buildPartyBar');
    guard(() => this.#buildQuestPanel(), 'buildQuestPanel');

    guard(() => this.#refreshPlayer(), 'refreshPlayer');
    guard(() => this.#refreshParty(), 'refreshParty');
    guard(() => this.#refreshQuest(), 'refreshQuest');
    guard(() => this.#layout(), 'layout');

    // live-update on the dataManager events each region depends on
    const onParty = () => {
      this.#refreshPlayer();
      this.#refreshParty();
      this.#layout();
    };
    const onProgress = () => this.#refreshQuest();
    const onGold = () => this.#refreshQuest();
    const onName = () => this.#refreshPlayer();
    const onSkin = () => this.#refreshPlayer();
    const onXp = () => this.#refreshPlayer();

    const ev = dataManager.store.events;
    ev.on(`changedata-${DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY}`, onParty, this);
    ev.on(`changedata-${DATA_MANAGER_STORE_KEYS.PROGRESS}`, onProgress, this);
    ev.on(`changedata-${DATA_MANAGER_STORE_KEYS.GOLD}`, onGold, this);
    ev.on(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_NAME}`, onName, this);
    ev.on(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_SKIN}`, onSkin, this);
    ev.on(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_XP}`, onXp, this);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);

    // surface the active api daily-quest objective when connected (optional, degrades offline)
    void this.#syncDailyObjective();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.#layout, this);
      ev.off(`changedata-${DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY}`, onParty, this);
      ev.off(`changedata-${DATA_MANAGER_STORE_KEYS.PROGRESS}`, onProgress, this);
      ev.off(`changedata-${DATA_MANAGER_STORE_KEYS.GOLD}`, onGold, this);
      ev.off(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_NAME}`, onName, this);
      ev.off(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_SKIN}`, onSkin, this);
      ev.off(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_XP}`, onXp, this);
    });
  }

  /**
   * Resolve the WorldScene and invoke one of its public open* methods. Single bridge so every
   * button shares the launch/pause behavior of the keyboard menu.
   * @param {string} method
   * @returns {void}
   */
  #invokeWorld(method) {
    // Re-entrancy guard: a fast double-tap (or pointerdown+pointerup both firing) must not launch
    // the same overlay twice. Cleared on the next tick so the next genuine press works.
    if (this.#menuBusy) {
      return;
    }
    const world = /** @type {import('./world-scene.js').WorldScene} */ (this.scene.get(SCENE_KEYS.WORLD_SCENE));
    if (world && typeof world[method] === 'function') {
      this.#menuBusy = true;
      this.time.delayedCall(250, () => {
        this.#menuBusy = false;
      });
      world[method]();
    }
  }

  /** @type {boolean} guards against double-launching an overlay on a fast/duplicate press */
  #menuBusy = false;

  /**
   * Draw a filled rounded-rect "glass" panel into a graphics object (crisp, pixel-friendly).
   * @param {Phaser.GameObjects.Graphics} g
   * @param {number} w
   * @param {number} h
   * @param {number} stroke accent color
   * @param {number} [fillAlpha=0.82]
   * @returns {void}
   */
  #drawGlass(g, w, h, stroke, fillAlpha = 0.96) {
    g.clear();
    // Solid wooden panel with a chunky 2-tone pixel border (thick ink outer + thin accent inner).
    g.fillStyle(GLASS, fillAlpha);
    g.fillRoundedRect(0, 0, w, h, RADIUS);
    g.lineStyle(3, INK, 1);
    g.strokeRoundedRect(0, 0, w, h, RADIUS);
    g.lineStyle(1.5, stroke, 1);
    g.strokeRoundedRect(2.5, 2.5, w - 5, h - 5, Math.max(1, RADIUS - 2));
  }

  // ── Top-left player card ────────────────────────────────────────────────────────

  /** @returns {void} */
  #buildPlayerCard() {
    const c = this.add.container(0, 0).setScrollFactor(0).setDepth(1000);

    const bg = this.add.graphics();
    this.#drawGlass(bg, CARD_W, CARD_H, EMERALD);

    // circular avatar: the player's overworld skin idle frame, masked to a disc
    const cx = PAD + AVATAR / 2;
    const cy = CARD_H / 2;
    const ring = this.add.graphics();
    ring.lineStyle(3, GOLD, 0.9);
    ring.strokeCircle(cx, cy, AVATAR / 2 + 2);

    this.#avatarImg = this.add.image(cx, cy, this.#playerSkinKey()).setOrigin(0.5);
    this.#fitAvatar();
    // Geometry masks are evaluated in screen space; the card container is pinned at (PAD, PAD)
    // with scrollFactor 0, so offset the disc by the container origin to align with the sprite.
    const maskG = this.make.graphics({ x: 0, y: 0 }, false);
    maskG.fillStyle(0xffffff);
    maskG.fillCircle(PAD + cx, PAD + cy, AVATAR / 2);
    this.#avatarImg.setMask(maskG.createGeometryMask());

    const textX = PAD + AVATAR + 12;

    this.#nameText = this.add.text(textX, 13, '', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '16px',
      color: '#eafff5',
    });
    // NOTE: no setResolution() here — it doubles the reported `.width`, which broke the
    // name-vs-Lv-badge fit math and let the name overlap the badge.

    // "Lv. N" gold badge, positioned after the name in #refreshPlayer()
    this.#lvText = this.add
      .text(0, 12, '', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '13px',
        color: '#0b1409',
        backgroundColor: '#f5b23e',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0, 0);

    // HP + XP bars (drawn each refresh)
    this.#hpBar = this.add.graphics();
    this.#xpBar = this.add.graphics();
    this.#hpText = this.add
      .text(CARD_W - PAD, 40, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '11px', color: '#aef0c4' })
      .setOrigin(1, 0.5);
    this.#xpText = this.add
      .text(CARD_W - PAD, 66, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '11px', color: '#bfeaff' })
      .setOrigin(1, 0.5);

    c.add([bg, ring, this.#avatarImg, this.#nameText, this.#lvText, this.#hpBar, this.#xpBar, this.#hpText, this.#xpText]);
    this.#playerCard = c;
  }

  /** @returns {string} the player's selected overworld skin asset key (falls back to skin 1). */
  #playerSkinKey() {
    const skin =
      dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_SKIN) ||
      dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR) ||
      'PLAYER_SKIN_1';
    return this.textures.exists(skin) ? skin : 'PLAYER_SKIN_1';
  }

  /** Crop the skin sheet to its DOWN-facing idle frame (index 1) and scale it crisply into the
   *  avatar disc. The MPWSP "ow*" sheets are 32px frames; frame 1 = neutral standing pose. */
  #fitAvatar() {
    const key = this.#playerSkinKey();
    this.#avatarImg.setTexture(key, 1);
    const fw = this.#avatarImg.frame.realWidth || 32;
    // fill the disc (slightly oversized so the character reads), pixel-crisp
    const scale = Math.ceil(((AVATAR + 6) / fw) * 100) / 100;
    this.#avatarImg.setScale(scale);
    this.#avatarImg.texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
  }

  /**
   * Derive the player's Lv / HP / XP from the lead party monster (a sensible, clearly-labeled
   * mapping: the HUD player level == lead monster level). Falls back to a Lv.1 full-HP card when
   * the party is empty so the card always renders.
   * @returns {void}
   */
  #refreshPlayer() {
    if (!this.#nameText) {
      return;
    }
    this.#fitAvatar();

    const name = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_NAME) || 'Hunter';
    /** @type {import('../types/typedef.js').Monster[]} */
    const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY) || [];
    const lead = party[0];

    // Derive real stats from the lead monster. A monster always carries currentHp/maxHp from the
    // roster; default to a full Lv.1 25/25 card only when there is genuinely no lead yet so the
    // bar never renders empty ("HP 0/100"). HP reads e.g. "25/25" at full health.
    const hasLead = !!lead;
    const maxHp = lead?.maxHp ?? 25;
    // clamp currentHp into [0, maxHp]; if it's missing on an existing monster treat it as full.
    const rawHp = lead?.currentHp;
    const hp = hasLead
      ? Phaser.Math.Clamp(typeof rawHp === 'number' ? rawHp : maxHp, 0, maxHp)
      : maxHp;

    // ACCOUNT level + XP (dot's "player lvl") — a real player-progression bar, NOT the lead monster's.
    const accountXp = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_XP) || 0;
    const accountLevel = playerLevel(accountXp);
    const lvBase = xpForLevel(accountLevel);
    const lvNext = xpForLevel(accountLevel + 1);
    const xpFrac = Phaser.Math.Clamp((accountXp - lvBase) / Math.max(1, lvNext - lvBase), 0, 1);
    const xpPct = Math.round(xpFrac * 100);

    // Pin the gold "Lv. N" badge to the card's RIGHT edge (a fixed anchor, robust to text-width
    // measurement quirks from setResolution), then truncate the name so it always ends BEFORE the
    // badge's left edge — guarantees no name/badge overlap on any name length.
    this.#lvText.setText(`Lv. ${accountLevel}`);
    this.#lvText.setOrigin(1, 0).setPosition(CARD_W - PAD, 13);
    const lvLeftEdge = CARD_W - PAD - this.#lvText.width;
    // hard char cap (belt-and-suspenders) then pixel-truncate to end before the badge.
    const capped = name.length > 12 ? `${name.slice(0, 12)}…` : name;
    this.#nameText.setText(this.#truncateToWidth(capped, this.#nameText, lvLeftEdge - 12 - this.#nameText.x));

    const barX = PAD + AVATAR + 12;
    const barW = CARD_W - barX - PAD;
    const hpFrac = maxHp > 0 ? Phaser.Math.Clamp(hp / maxHp, 0, 1) : 1;

    this.#drawBar(this.#hpBar, barX, 38, barW, 9, hpFrac, HP_GREEN);
    this.#drawBar(this.#xpBar, barX, 64, barW, 9, xpFrac, XP_BLUE);
    this.#hpText.setText(`HP ${hp}/${maxHp}`);
    this.#xpText.setText(`XP ${xpPct}%`);
  }

  /**
   * Truncate `text` (adding an ellipsis) so it measures within `maxWidth` using the given Text
   * object's font metrics. Returns the original when it already fits. Keeps labels on one line so
   * they never overflow or jitter.
   * @param {string} text
   * @param {Phaser.GameObjects.Text} measurer
   * @param {number} maxWidth
   * @returns {string}
   */
  #truncateToWidth(text, measurer, maxWidth) {
    measurer.setText(text);
    if (measurer.width <= maxWidth) {
      return text;
    }
    let s = text;
    while (s.length > 1) {
      s = s.slice(0, -1);
      measurer.setText(`${s}…`);
      if (measurer.width <= maxWidth) {
        return `${s}…`;
      }
    }
    return `${s}…`;
  }

  /**
   * Draw a rounded track + fill bar (HP/XP) into a graphics object.
   * @param {Phaser.GameObjects.Graphics} g
   * @param {number} x
   * @param {number} y
   * @param {number} w
   * @param {number} h
   * @param {number} frac 0..1
   * @param {number} color fill color
   * @returns {void}
   */
  #drawBar(g, x, y, w, h, frac, color) {
    g.clear();
    g.fillStyle(0x0b1409, 0.9);
    g.fillRoundedRect(x, y, w, h, h / 2);
    if (frac > 0) {
      g.fillStyle(color, 1);
      g.fillRoundedRect(x, y, Math.max(h, w * frac), h, h / 2);
    }
    g.lineStyle(1, 0xffffff, 0.12);
    g.strokeRoundedRect(x, y, w, h, h / 2);
  }

  // ── Left vertical icon menu ──────────────────────────────────────────────────────

  /** @returns {void} */
  #buildMenuColumn() {
    const c = this.add.container(0, 0).setScrollFactor(0).setDepth(1000);
    this.#menuButtons = MENU_ITEMS.map((item, i) => {
      const btn = this.#makeIconButton(item.icon, item.label, () => this.#invokeWorld(item.method));
      btn.setPosition(0, i * (ICON_BTN + ICON_GAP));
      c.add(btn);
      return btn;
    });
    this.#menuColumn = c;
  }

  /**
   * Reflow the icon buttons so the menu never runs off the bottom of a short (e.g. landscape phone)
   * viewport, where the lower items would render under the fold / party bar and become untappable.
   * Wraps into as many columns as needed to fit the available height (capped so it can't sprawl
   * across the whole width). On a tall (desktop/portrait) viewport the whole stack fits in one
   * column, so this is a no-op there. Returns the laid-out menu's scaled footprint (from the menu
   * container's anchored top-left) so the quest panel can avoid overlapping it.
   * @param {number} availableH vertical space the menu may occupy below its top (screen px)
   * @param {number} s the menu container's scale
   * @param {boolean} allowWrap gate: only wrap into extra columns on small/short viewports so
   *   desktop (tall) stays an unchanged single column even if the design height is tight.
   * @returns {{ width: number, height: number }} scaled footprint of the menu
   */
  #layoutMenuColumn(availableH, s, allowWrap) {
    const step = ICON_BTN + ICON_GAP;
    const n = this.#menuButtons.length;
    // How many rows fit in the available height (in unscaled local units, since the container scales).
    const localAvail = availableH / s;
    const maxRows = Math.max(1, Math.floor((localAvail + ICON_GAP) / step));
    // Minimum columns to fit every item in the available rows; cap at 3 so it stays a tidy block.
    // Desktop / tall viewports keep the original single column (allowWrap === false).
    const cols = allowWrap ? Math.min(3, Math.max(1, Math.ceil(n / maxRows))) : 1;
    const rows = Math.ceil(n / cols);
    this.#menuButtons.forEach((btn, i) => {
      const col = Math.floor(i / rows);
      const row = i % rows;
      btn.setPosition(col * step, row * step);
    });
    return {
      width: ((cols - 1) * step + ICON_BTN) * s,
      height: (Math.min(rows, n) * step - ICON_GAP) * s,
    };
  }

  /**
   * A square glass icon button with a small label, hover/press feedback, and a big tap target.
   * @param {string} icon emoji glyph
   * @param {string} label
   * @param {() => void} onPress
   * @returns {Phaser.GameObjects.Container}
   */
  #makeIconButton(icon, label, onPress) {
    const container = this.add.container(0, 0);
    const bg = this.add.graphics();
    this.#drawGlass(bg, ICON_BTN, ICON_BTN, EMERALD);

    const iconText = this.add
      .text(ICON_BTN / 2, ICON_BTN / 2 - 6, icon, { fontFamily: 'system-ui, sans-serif', fontSize: '22px', color: '#eafff5' })
      .setOrigin(0.5);
    const labelText = this.add
      .text(ICON_BTN / 2, ICON_BTN - 11, label, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '10px',
        color: '#aef0c4',
      })
      .setOrigin(0.5);

    container.add([bg, iconText, labelText]);
    container.setSize(ICON_BTN, ICON_BTN);
    // Extend the clickable area into the gaps around the icon so there are no dead zones between
    // buttons (a tap landing slightly off the 56px icon — e.g. on VAULT — was missing entirely).
    container.setInteractive(
      new Phaser.Geom.Rectangle(-ICON_GAP / 2, -ICON_GAP / 2, ICON_BTN + ICON_GAP, ICON_BTN + ICON_GAP),
      Phaser.Geom.Rectangle.Contains,
    );

    container.on(Phaser.Input.Events.POINTER_OVER, () => {
      this.#drawGlass(bg, ICON_BTN, ICON_BTN, GOLD, 0.95);
      labelText.setColor('#ffe9b8');
      this.input.setDefaultCursor('pointer');
    });
    container.on(Phaser.Input.Events.POINTER_OUT, () => {
      this.#drawGlass(bg, ICON_BTN, ICON_BTN, EMERALD);
      labelText.setColor('#aef0c4');
      container.setScale(1);
      this.input.setDefaultCursor('default');
    });
    // Fire on POINTER_DOWN for an immediate, snappy response (no wait for the up event); the press
    // scale is restored on the next tick so the tap still reads as a press. Re-entrancy is guarded
    // in #invokeWorld so a stray POINTER_UP can't double-trigger.
    container.on(Phaser.Input.Events.POINTER_DOWN, () => {
      container.setScale(0.92);
      onPress();
      this.time.delayedCall(90, () => container.setScale(1));
    });
    return container;
  }

  // ── Bottom-left party bar ────────────────────────────────────────────────────────

  /** @returns {void} */
  #buildPartyBar() {
    this.#partyBar = this.add.container(0, 0).setScrollFactor(0).setDepth(1000);
    // Toggle the dropdown via a SCENE-LEVEL pointer check against the header bounds. The per-object
    // hit area proved unreliable under the stacked HUD scenes (the tap fell through), so we test the
    // header rectangle manually. Ground tap-to-move is disabled, so this never moves the player.
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer) => {
      const b = this.#partyBar;
      if (!b) return;
      const w = CHIP_W * b.scaleX;
      const h = PARTY_HEADER_H * b.scaleY;
      if (pointer.x >= b.x && pointer.x <= b.x + w && pointer.y >= b.y && pointer.y <= b.y + h) {
        this.#partyExpanded = !this.#partyExpanded;
        this.#refreshParty();
      }
    });
  }

  /**
   * Rebuild the party chips from the real MONSTERS_IN_PARTY (all 6 shown, STACKED VERTICALLY on the
   * right edge). Each chip = the monster's mini-sprite (its assetKey, fit crisply into a box) + NAME
   * + "Lv. N". Vertical stacking lets players see their whole team at a glance.
   * @returns {void}
   */
  #refreshParty() {
    if (!this.#partyBar) {
      return;
    }
    this.#partyBar.removeAll(true);

    /** @type {import('../types/typedef.js').Monster[]} */
    const party = (dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY) || []).filter(
      (m) => m && typeof m.monsterId === 'number', // a null slot here crashed the team widget on tap (mobile)
    );
    const count = Math.min(6, party.length);

    // Always-visible toggle button (TEAM N/6 ▾). Tapping it expands/collapses the chip list — a
    // dropdown so the full team never overlaps the player card on narrow viewports.
    const header = this.#makePartyHeader(count);
    header.setPosition(0, 0);
    this.#partyBar.add(header);

    if (this.#partyExpanded) {
      party.slice(0, 6).forEach((monster, i) => {
        const chip = this.#makePartyChip(monster);
        chip.setPosition(0, PARTY_HEADER_H + PARTY_HEADER_GAP + i * (CHIP_H + CHIP_GAP));
        this.#partyBar.add(chip);
      });
    }

    // re-anchor (panel height changed with the expand state / count)
    this.#layout();
  }

  /**
   * The party dropdown's toggle button: "TEAM  N/6  ▾/▴", tap to expand/collapse the chip list.
   * @param {number} count
   * @returns {Phaser.GameObjects.Container}
   */
  #makePartyHeader(count) {
    const container = this.add.container(0, 0);
    const bg = this.add.graphics();
    this.#drawGlass(bg, CHIP_W, PARTY_HEADER_H, GOLD);
    const label = this.add
      .text(12, PARTY_HEADER_H / 2, `TEAM  ${count}/6`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '14px',
        color: '#fff4d6',
      })
      .setOrigin(0, 0.5)
      .setResolution(2);
    const arrow = this.add
      .text(CHIP_W - 12, PARTY_HEADER_H / 2, this.#partyExpanded ? '▲' : '▼', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '12px',
        color: '#fff4d6',
      })
      .setOrigin(1, 0.5)
      .setResolution(2);
    container.add([bg, label, arrow]);
    // NB: the click handling is done at the scene level in #buildPartyBar (a manual bounds check) —
    // a per-object hit area on this header fell through under the overlapping HUD scenes.
    return container;
  }

  /**
   * @param {import('../types/typedef.js').Monster} monster
   * @returns {Phaser.GameObjects.Container}
   */
  #makePartyChip(monster) {
    const container = this.add.container(0, 0);
    const bg = this.add.graphics();
    this.#drawGlass(bg, CHIP_W, CHIP_H, EMERALD);

    // mini-sprite fit into a square box on the left of the chip (crisp), with comfortable padding
    const box = CHIP_H - 18;
    const cx = 9 + box / 2;
    const cy = CHIP_H / 2;
    let sprite;
    if (monster.assetKey && this.textures.exists(monster.assetKey)) {
      sprite = this.add.image(cx, cy, monster.assetKey).setOrigin(0.5);
      const src = this.textures.get(monster.assetKey).getSourceImage();
      const fit = box / Math.max(src.width || box, src.height || box);
      sprite.setScale(fit);
    }

    const textX = box + 16;
    const nameText = this.add
      .text(textX, 7, '', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '13px',
        color: '#eafff5',
      })
      .setResolution(2);
    // single-line, truncated to the chip width (no wordWrap → no 2-line reflow/jitter)
    nameText.setText(this.#truncateToWidth(monster.name ?? 'Monster', nameText, CHIP_W - textX - 12));
    const lvText = this.add
      .text(textX, 25, `Lv. ${monster.currentLevel ?? 1}`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '12px',
        color: '#7af0c0',
      })
      .setResolution(2);

    // HP bar — see at a glance who's healthy / low / fainted (green > yellow > red, grey when fainted).
    const hp = Math.max(0, Number(monster.currentHp) || 0);
    const maxHp = Math.max(1, Number(monster.maxHp) || 1);
    const pct = Math.max(0, Math.min(1, hp / maxHp));
    const barW = CHIP_W - textX - 14;
    const barY = CHIP_H - 14;
    const hpBar = this.add.graphics();
    hpBar.fillStyle(0x000000, 0.5).fillRoundedRect(textX, barY, barW, 6, 3);
    const hpColor = hp <= 0 ? 0x6b6b6b : pct > 0.5 ? 0x6ce06a : pct > 0.2 ? 0xe6c84a : 0xe6604a;
    if (pct > 0) hpBar.fillStyle(hpColor, 1).fillRoundedRect(textX, barY, Math.max(4, barW * pct), 6, 3);

    /** @type {Phaser.GameObjects.GameObject[]} */
    const parts = [bg];
    if (sprite) {
      parts.push(sprite);
    }
    parts.push(nameText, lvText, hpBar);
    container.add(parts);
    return container;
  }

  // ── Bottom-right "Current Quest" panel ──────────────────────────────────────────

  /** @returns {void} */
  #buildQuestPanel() {
    const c = this.add.container(0, 0).setScrollFactor(0).setDepth(1000);
    const bg = this.add.graphics();
    this.#drawGlass(bg, QUEST_W, QUEST_H, GOLD);

    const pin = this.add.text(14, 12, '📍', { fontFamily: 'system-ui, sans-serif', fontSize: '18px' }).setOrigin(0, 0);
    const eyebrow = this.add.text(40, 14, 'CURRENT QUEST', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '12px',
      color: '#f5b23e',
    });
    this.#questText = this.add.text(14, 40, '', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '15px',
      color: '#ffffff',
      wordWrap: { width: QUEST_W - 28 },
    });

    c.add([bg, pin, eyebrow, this.#questText]);
    this.#questPanel = c;
  }

  /**
   * Recompute the active objective: the first incomplete step in the chain (reads the persisted
   * progress counters), and persist the current step so it survives reload.
   * @returns {void}
   */
  #refreshQuest() {
    if (!this.#questText) {
      return;
    }
    const progress = dataManager.getProgress();
    const activeIndex = OBJECTIVE_CHAIN.findIndex((o) => !o.done(progress));

    if (activeIndex === -1) {
      this.#questText.setText('All goals complete! ⭐');
      if (progress.step !== OBJECTIVE_CHAIN.length) {
        dataManager.setProgress({ step: OBJECTIVE_CHAIN.length });
      }
      return;
    }

    const objective = OBJECTIVE_CHAIN[activeIndex];
    this.#questText.setText(`${objective.label}   ${objective.progress(progress)}`);
    if (progress.step !== activeIndex) {
      dataManager.setProgress({ step: activeIndex });
    }
  }

  /**
   * Optionally surface the active api daily-quest objective when the player is connected.
   * Best-effort: degrades silently offline.
   * @returns {Promise<void>}
   */
  async #syncDailyObjective() {
    const quests = await fetchDailyQuests(netManager.wallet);
    // the scene may have been shut down while the fetch was in flight — bail before any setText
    if (!this.sys.isActive()) {
      return;
    }
    // Array.isArray guard is CRITICAL: fetchDailyQuests can resolve to a non-array (an error/object
    // payload from the api), and `.length` on it is undefined (not 0), so the old guard let it through
    // and `quests.find(...)` threw "t.find is not a function" (the red error bar). Bail on any non-array.
    if (!Array.isArray(quests) || quests.length === 0 || !this.#questText) {
      return;
    }
    const active = quests.find((q) => !q.progress?.completed) ?? quests[0];
    if (!active?.def) {
      return;
    }
    const done = active.progress?.progress ?? 0;
    const target = active.def.goal?.count ?? 0;
    this.#questText.setText(`Daily: ${active.def.title}   ${Math.min(done, target)}/${target}`);
  }

  // ── Layout (responsive) ─────────────────────────────────────────────────────────

  /**
   * Reflow on viewport resize: player card top-left, icon menu down the left edge under it,
   * party bar bottom-left, quest panel bottom-right. Reads this.scale.width/height.
   * @returns {void}
   */
  #layout() {
    const sw = this.scale.width;
    const sh = this.scale.height;
    if (!this.#playerCard) {
      return;
    }

    // Small-screen scaling: shrink the icon menu, party chips + quest panel on narrow phones so
    // they stay thumb-sized and fit. Each is top/bottom-left or bottom-right anchored, so we scale
    // about its anchored corner and use the SCALED size for bottom/right placement (nothing is cut
    // off). The player card is left UNSCALED (its avatar disc uses a screen-space geometry mask
    // that wouldn't follow a container scale); at 264px it already fits a 390px portrait width.
    // Desktop (>=560 wide) keeps scale 1.
    const minDim = Math.min(sw, sh);
    const s = minDim <= 380 ? 0.72 : minDim <= 480 ? 0.82 : sw < 560 ? 0.9 : 1;
    [this.#menuColumn, this.#partyBar, this.#questPanel].forEach((c) => c.setScale(s));

    // top-left player card (unscaled)
    this.#playerCard.setPosition(PAD, PAD);

    // left vertical icon menu, just under the player card
    const menuTop = PAD + CARD_H + 14;
    this.#menuColumn.setPosition(PAD, menuTop);

    // The virtual joystick (bottom-LEFT) + A/≡ buttons (bottom-RIGHT) show whenever the mobile
    // controls are active. That condition MUST match world-scene's #updateMobileControls — `coarse
    // pointer OR a narrow viewport (min side ≤ 820)` — otherwise the HUD fails to clear controls that
    // are actually on screen (the old `isTouch`-only check missed the narrow-non-touch case → party
    // bar overlapped the joystick). Reserve the footprint on BOTH sides so neither the party bar (left)
    // nor the quest panel (right) ever sits under a control button.
    const coarse =
      typeof window !== 'undefined' &&
      ((window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window);
    const mobileControlsActive = !!coarse || Math.min(sw, sh) <= 820;
    const joyClearance = mobileControlsActive ? 175 : 0;
    const rightBtnClearance = mobileControlsActive ? 175 : 0;

    // RIGHT-edge team DROPDOWN, anchored BELOW the top-right HUD band (the currency panel + minimap,
    // which live in the separate currency-hud scene). Right-aligned + scaled so it always stays fully
    // on-screen and never overlaps the top panels — on any viewport, portrait or landscape.
    const partyX = sw - CHIP_W * s - PAD;
    const partyTop = PAD + TOP_RIGHT_BAND * s;
    this.#partyBar.setPosition(partyX, partyTop);
    const partyCountShown = Math.min(6, (dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY) || []).length);
    const partyStackH =
      (PARTY_HEADER_H +
        (this.#partyExpanded
          ? PARTY_HEADER_GAP + partyCountShown * CHIP_H + Math.max(0, partyCountShown - 1) * CHIP_GAP
          : 0)) *
      s;

    // Reflow the icon menu so it never runs under the fold / party bar on short (landscape) phones:
    // clamp it to the space between the player card and the bottom HUD band (party bar height +
    // joystick clearance), wrapping into extra columns if a single column won't fit. On tall
    // (desktop/portrait) viewports the whole stack fits, so this stays a single column.
    const bottomBand = joyClearance + PAD;
    const menuAvailableH = Math.max(ICON_BTN, sh - bottomBand - menuTop);
    // Only reflow on constrained viewports: short (landscape phone) or scaled-down (narrow phone).
    // A short viewport = the full single column wouldn't fit below the player card. Desktop stays 1 col.
    const singleColH = this.#menuButtons.length * (ICON_BTN + ICON_GAP) - ICON_GAP;
    const allowWrap = s < 1 || menuAvailableH < singleColH;
    const menuFootprint = this.#layoutMenuColumn(menuAvailableH, s, allowWrap);

    // bottom-right quest panel; lift it above the A/≡ control buttons (bottom-right) when the mobile
    // controls are active, and above the party bar on narrow phones — so it never sits under either.
    let questX = sw - QUEST_W * s - PAD;
    const questY = sh - PAD - QUEST_H * s - rightBtnClearance;
    // The party column now occupies the top-right. If the bottom-right quest panel would reach up into
    // it on a short viewport, move the quest panel to the bottom-LEFT (free now that the party bar left
    // it) — desktop only, since on mobile that corner holds the virtual joystick.
    if (!mobileControlsActive && questY < partyTop + partyStackH + 12) {
      questX = PAD;
    }
    // Never let the quest panel overlap the icon menu column: if it would intrude into the menu's
    // horizontal band (e.g. a 2-column menu on a short landscape phone pushes toward the right edge),
    // clamp its top so it sits below the menu's footprint.
    const menuBottom = menuTop + menuFootprint.height;
    const menuRight = PAD + menuFootprint.width;
    if (questX < menuRight + 12 && questY < menuBottom + 12) {
      questY = Math.min(menuBottom + 12, sh - PAD - QUEST_H * s);
    }
    this.#questPanel.setPosition(questX, questY);
  }
}
